// Recipient-address validation, browser-safe (no tronweb / node crypto).

import { base58check } from '@scure/base';
import { sha256 } from '@noble/hashes/sha256';
import type { Chain } from '@incpt/shared';
import { isValidBtcAddress } from './btc';
import { isValidSolanaAddress } from './solana';
import { isValidZcashAddress } from './zcash';
import { isValidMoneroAddress } from './monero';
import { isValidTonAddress } from './ton/address';
import { isValidDogeAddress } from './doge';
import { isValidXrpAddress } from './xrp';

const b58check = base58check(sha256);
const EVM_RE = /^0x[0-9a-fA-F]{40}$/;
const TRON_PREFIX = 0x41;

/** Whether `address` is a well-formed address for the given chain.
 *  Exhaustive switch — a new chain MUST get an explicit branch here, never a
 *  fallthrough (an if/else fallthrough once validated btc strings as Tron). */
export function isValidAddress(chain: Chain, address: string): boolean {
  switch (chain) {
    case 'eth':
    case 'bsc':
    case 'hyperevm':
      // Same 20-byte address space, and deliberately interchangeable: sending
      // to a BSC address is sending to an Ethereum one. What keeps the funds
      // on the right chain is the chain the transfer is built for, not the
      // string.
      return EVM_RE.test(address);
    case 'trx':
      // Tron: base58check decodes to 21 bytes with a 0x41 prefix byte.
      try {
        const decoded = b58check.decode(address);
        return decoded.length === 21 && decoded[0] === TRON_PREFIX;
      } catch {
        return false;
      }
    case 'sol':
      return isValidSolanaAddress(address);
    case 'ton':
      return isValidTonAddress(address);
    case 'btc':
      return isValidBtcAddress(address);
    case 'zec':
      return isValidZcashAddress(address);
    case 'xmr':
      return isValidMoneroAddress(address);
    case 'doge':
      return isValidDogeAddress(address);
    case 'xrp':
      // Classic 'r…' addresses only. An 'X…' address is valid on this ledger
      // but packs a destination tag the send path cannot honour, so it is
      // rejected here rather than delivered to the right account with the
      // wrong tag — see isXrpXAddress.
      return isValidXrpAddress(address);
    default: {
      const never: never = chain;
      throw new Error(`unsupported chain ${String(never)}`);
    }
  }
}
